/**
 * HarnessRunnerPanel — §19.1 dynamic harness runner.
 *
 * Replaces the hardcoded "Run selftest" button. Flow:
 *   1. Picker populated from the catalogue (GET /harnesses, passed in).
 *   2. On select → GET /harnesses/:id (typed manifest via harn-client).
 *   3. Render manifest header + a form from `input_schema`
 *      (<HarnessInputForm>).
 *   4. Required admin "reason" field (5–500 chars, Decision 3).
 *   5. Topology shown read-only (single-instance; §19.2 generalizes).
 *   6. Client-side size guard vs `resources.max_input_bytes`
 *      (consumer-guide §5.1; server 413s otherwise).
 *   7. Submit → runHarness(..., { triggerReason }) which attaches the
 *      `X-Admin-Trigger-Reason` header (§19.4 wire contract).
 *
 * Result/stream rendering stays in HarnessesBrowser's live-stream panel;
 * this component just starts the task and hands the id back via
 * `onTaskStarted`.
 */

import { useCallback, useMemo, useState } from "react";
import { Loader2, Play, ShieldAlert } from "lucide-react";
import type { HarnClient, HarnessManifest } from "@/lib/harn-client";
import { HarnApiError } from "@/lib/harn-client";
import type { HarnessManifestSummary } from "@/types/harn";
import { HarnessInputForm, isRenderableSchema } from "./HarnessInputForm";

const REASON_MIN = 5;
const REASON_MAX = 500;

export interface HarnessRunnerPanelProps {
  client: HarnClient;
  harnesses: HarnessManifestSummary[];
  /** Org for the topology triple. Gateway mode: the selected runtime's
   *  org; direct mode: a dev placeholder. Resolved by the parent. */
  topologyOrgId: string;
  onTaskStarted: (taskId: string) => void;
}

const DEV_TRIPLE = {
  clientId: "client-dev-001",
  matterId: "matter-dev-001",
  caseId: "case-dev-001",
};

function byteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

export function HarnessRunnerPanel({
  client,
  harnesses,
  topologyOrgId,
  onTaskStarted,
}: HarnessRunnerPanelProps) {
  const [selectedId, setSelectedId] = useState("");
  const [manifest, setManifest] = useState<HarnessManifest | null>(null);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [formValue, setFormValue] = useState<Record<string, unknown>>({});
  const [rawText, setRawText] = useState("{}");
  const [reason, setReason] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reasonError =
    reason.trim().length === 0
      ? "Reason is required for admin-triggered runs"
      : reason.trim().length < REASON_MIN
        ? `Reason must be at least ${REASON_MIN} characters`
        : reason.length > REASON_MAX
          ? `Reason must be at most ${REASON_MAX} characters`
          : null;

  const onSelect = useCallback(
    async (id: string) => {
      setSelectedId(id);
      setManifest(null);
      setFormValue({});
      setRawText("{}");
      setFieldErrors({});
      setError(null);
      if (!id) return;
      setManifestLoading(true);
      try {
        const m = await client.getHarness(id);
        setManifest(m);
      } catch (e) {
        setError(
          e instanceof HarnApiError
            ? `Failed to load manifest (${e.status})`
            : e instanceof Error
              ? e.message
              : "Failed to load manifest",
        );
      } finally {
        setManifestLoading(false);
      }
    },
    [client],
  );

  // Resolve the input payload: native form value, or parsed raw JSON when
  // the schema wasn't form-renderable.
  const resolveInput = useCallback((): { ok: true; input: unknown } | { ok: false; msg: string } => {
    const schema = manifest?.input_schema as Record<string, unknown> | undefined;
    // Same predicate the form used — if the form rendered native fields
    // we submit `formValue`; if it fell back to the raw textarea we
    // submit the parsed JSON. A looser check here would submit {} when
    // the user actually typed raw JSON.
    if (isRenderableSchema(schema)) return { ok: true, input: formValue };
    try {
      return { ok: true, input: rawText.trim() === "" ? {} : JSON.parse(rawText) };
    } catch {
      return { ok: false, msg: "Raw input is not valid JSON" };
    }
  }, [manifest, formValue, rawText]);

  const handleRun = useCallback(async () => {
    if (!manifest || reasonError) return;
    setError(null);
    setFieldErrors({});

    const resolved = resolveInput();
    if (!resolved.ok) {
      setFieldErrors({ __root__: resolved.msg });
      return;
    }

    // Client-side size guard (consumer-guide §5.1).
    const maxBytes = manifest.resources?.max_input_bytes ?? 65_536;
    const inputBytes = byteLength(JSON.stringify(resolved.input));
    if (inputBytes > maxBytes) {
      setError(
        `Input is ${inputBytes} bytes — exceeds this harness's max_input_bytes (${maxBytes}). Trim the payload.`,
      );
      return;
    }

    setSubmitting(true);
    try {
      const res = await client.runHarness(
        manifest.id,
        {
          orgId: topologyOrgId,
          ...DEV_TRIPLE,
          input: resolved.input,
        },
        { triggerReason: reason.trim() },
      );
      onTaskStarted(res.taskId);
    } catch (e) {
      if (e instanceof HarnApiError) {
        if (e.status === 413) {
          setError("Server rejected the payload as too large (413).");
        } else if (e.status === 400) {
          // schema_validation_failed → { error, details|message }
          let detail = e.body;
          try {
            const parsed = JSON.parse(e.body) as { details?: string; message?: string };
            detail = parsed.details ?? parsed.message ?? e.body;
          } catch {
            /* keep raw body */
          }
          setFieldErrors({ __root__: `Schema validation failed: ${detail}` });
        } else {
          setError(`Run failed (${e.status}): ${e.body.slice(0, 240)}`);
        }
      } else {
        setError(e instanceof Error ? e.message : "Failed to start harness");
      }
    } finally {
      setSubmitting(false);
    }
  }, [manifest, reason, reasonError, resolveInput, client, topologyOrgId, onTaskStarted]);

  const harnessOptions = useMemo(
    () => harnesses.map((h) => ({ id: h.id, label: h.tool?.label ?? h.id })),
    [harnesses],
  );

  return (
    <div className="space-y-3" data-testid="harness-runner-panel">
      {/* Picker */}
      <div className="space-y-1">
        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
          Harness
        </label>
        <select
          value={selectedId}
          onChange={(e) => onSelect(e.target.value)}
          disabled={submitting || harnesses.length === 0}
          className="w-full rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-indigo-400 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900"
          data-testid="harness-runner-picker"
        >
          <option value="">— select a harness —</option>
          {harnessOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label} ({o.id})
            </option>
          ))}
        </select>
      </div>

      {manifestLoading && (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading manifest…
        </div>
      )}

      {manifest && !manifestLoading && (
        <>
          {/* Manifest header */}
          <div className="rounded-md bg-slate-50 p-2.5 text-xs dark:bg-slate-800/60">
            <div className="font-medium text-slate-700 dark:text-slate-200">
              {manifest.tool.label}
            </div>
            <p className="mt-0.5 text-slate-500 dark:text-slate-400">
              {manifest.tool.description}
            </p>
            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-500">
              <span>
                perm: <span className="font-mono">{manifest.tool.permission_group}</span>
              </span>
              <span>
                scope: <span className="font-mono">{manifest.scope}</span>
              </span>
              <span>
                timeout:{" "}
                <span className="font-mono">
                  {manifest.resources?.default_timeout_ms ?? 120_000}ms
                </span>
              </span>
              <span>
                max input:{" "}
                <span className="font-mono">
                  {manifest.resources?.max_input_bytes ?? 65_536}B
                </span>
              </span>
            </div>
          </div>

          {/* Input form */}
          <HarnessInputForm
            schema={manifest.input_schema as Record<string, unknown> | undefined}
            value={formValue}
            onChange={setFormValue}
            rawText={rawText}
            onRawTextChange={setRawText}
            fieldErrors={fieldErrors}
            disabled={submitting}
          />

          {/* Topology (read-only, single-instance — §19.2 generalizes) */}
          <div className="rounded-md border border-dashed border-slate-200 p-2 text-[11px] text-slate-500 dark:border-slate-700">
            <div className="mb-1 font-medium text-slate-600 dark:text-slate-300">
              Topology
              <span className="ml-1 font-normal text-amber-500">
                v1.1: cross-tenant picker (§19.2)
              </span>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono">
              <span>org: {topologyOrgId}</span>
              <span>client: {DEV_TRIPLE.clientId}</span>
              <span>matter: {DEV_TRIPLE.matterId}</span>
              <span>case: {DEV_TRIPLE.caseId}</span>
            </div>
          </div>

          {/* Reason (required, audited) */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
              Reason for this run<span className="ml-0.5 text-red-500">*</span>
              <span className="ml-1 font-normal text-slate-400">
                — recorded in the admin audit trail (§19.4)
              </span>
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              maxLength={REASON_MAX + 50}
              placeholder="e.g. canary check for v1.2.0 rollout"
              className="w-full rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-indigo-400 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900"
              data-testid="harness-runner-reason"
            />
            {reason.length > 0 && reasonError && (
              <p className="text-[11px] text-red-600 dark:text-red-400">{reasonError}</p>
            )}
          </div>

          {fieldErrors.__root__ && (
            <div className="flex items-start gap-1.5 rounded-md bg-red-50 p-2 text-[11px] text-red-700 dark:bg-red-900/20 dark:text-red-400">
              <ShieldAlert className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{fieldErrors.__root__}</span>
            </div>
          )}
          {error && (
            <div className="rounded-md bg-red-50 p-2 text-[11px] text-red-700 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleRun}
            disabled={submitting || !!reasonError}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="harness-runner-run"
          >
            {submitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
            Run harness
          </button>
        </>
      )}
    </div>
  );
}
